import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import countryService from '../../services/countryService';
import { useCountry } from '../../hooks/useCountry';
import { Spinner } from '../ui';
import { COUNTRY_TESTIMONIALS } from '../../config/testimonials';

// Decorative SVG patterns per country
const PatternOverlay = ({ pattern, primaryColor }) => {
  const patterns = {
    stars: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200" fill={primaryColor}>
        {[...Array(5)].map((_, i) => (
          <polygon key={i} points="100,10 40,198 190,78 10,78 160,198" transform={`translate(${i * 30 - 60}, ${i * 20 - 40}) scale(0.15)`} />
        ))}
      </svg>
    ),
    crosshatch: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200">
        <pattern id="crosshatch" width="20" height="20" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="20" y2="20" stroke={primaryColor} strokeWidth="1" />
          <line x1="20" y1="0" x2="0" y2="20" stroke={primaryColor} strokeWidth="1" />
        </pattern>
        <rect width="200" height="200" fill="url(#crosshatch)" />
      </svg>
    ),
    maple: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200" fill={primaryColor}>
        <path d="M100 20 L110 60 L140 50 L120 80 L150 90 L120 100 L140 130 L110 110 L100 150 L90 110 L60 130 L80 100 L50 90 L80 80 L60 50 L90 60 Z" transform="translate(20, 10) scale(0.8)" />
      </svg>
    ),
    fleurdelis: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200" fill={primaryColor}>
        <ellipse cx="100" cy="60" rx="15" ry="40" />
        <ellipse cx="65" cy="80" rx="15" ry="35" transform="rotate(-30, 65, 80)" />
        <ellipse cx="135" cy="80" rx="15" ry="35" transform="rotate(30, 135, 80)" />
        <rect x="90" y="100" width="20" height="60" rx="5" />
      </svg>
    ),
    geometric: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200" fill="none" stroke={primaryColor} strokeWidth="2">
        <rect x="40" y="40" width="50" height="50" />
        <rect x="110" y="40" width="50" height="50" />
        <rect x="40" y="110" width="50" height="50" />
        <rect x="110" y="110" width="50" height="50" />
        <line x1="65" y1="90" x2="65" y2="110" />
        <line x1="135" y1="90" x2="135" y2="110" />
        <line x1="90" y1="65" x2="110" y2="65" />
        <line x1="90" y1="135" x2="110" y2="135" />
      </svg>
    ),
    arches: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200" fill="none" stroke={primaryColor} strokeWidth="2">
        {[0, 60, 120].map((x) => (
          <path key={x} d={`M${x + 10} 180 Q${x + 40} 40 ${x + 70} 180`} />
        ))}
      </svg>
    ),
    mosaic: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200" fill={primaryColor}>
        {[0, 50, 100, 150].map((x) =>
          [0, 50, 100, 150].map((y) => (
            <rect key={`${x}-${y}`} x={x + 5} y={y + 5} width="40" height="40" rx="8" opacity={(x + y) % 100 === 0 ? 0.8 : 0.4} />
          ))
        )}
      </svg>
    ),
    waves: (
      <svg className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" viewBox="0 0 200 200" fill="none" stroke={primaryColor} strokeWidth="2">
        {[40, 80, 120, 160].map((y) => (
          <path key={y} d={`M0 ${y} Q50 ${y - 30} 100 ${y} Q150 ${y + 30} 200 ${y}`} />
        ))}
      </svg>
    ),
  };
  return patterns[pattern] || null;
};

// Feature Card with themed accent
const FeatureCard = ({ icon, iconBg, title, description, accentColor }) => (
  <div className="group relative">
    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}05)` }} />
    <div className="relative bg-white rounded-2xl p-5 sm:p-6 h-full transition-all duration-300 group-hover:shadow-lg border border-gray-100 group-hover:border-opacity-0" style={{ '--card-accent': accentColor }}>
      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300`} style={{ backgroundColor: `${accentColor}15` }}>
        {React.cloneElement(icon, { style: { color: accentColor } })}
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm sm:text-base">{description}</p>
    </div>
  </div>
);

// Step Card Component with theme
const StepCard = ({ number, title, description, isLast, theme }) => (
  <div className="relative">
    <div className="bg-white rounded-2xl shadow-card p-5 sm:p-8 text-center hover:shadow-elevated transition-all duration-300 hover:-translate-y-1">
      <div className="w-12 h-12 sm:w-16 sm:h-16 text-white rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl font-bold mx-auto mb-3 sm:mb-4" style={{ background: theme?.colors?.buttonBg || 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: `0 10px 25px -5px ${theme?.colors?.primary || '#6366f1'}40` }}>
        {number}
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm sm:text-base">{description}</p>
    </div>
    {!isLast && (
      <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
        <svg className="w-8 h-8" style={{ color: `${theme?.colors?.primary || '#6366f1'}80` }} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
    )}
  </div>
);

// Region Badge Component with theme
const RegionBadge = ({ region, theme }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 text-center font-medium text-gray-700 text-sm sm:text-base border border-gray-100 transition-all duration-200 cursor-pointer hover:shadow-md" style={{ '--badge-hover-border': theme?.colors?.primary, '--badge-hover-bg': `${theme?.colors?.primary}10` }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = theme?.colors?.primary || '#6366f1'; e.currentTarget.style.backgroundColor = `${theme?.colors?.primary || '#6366f1'}10`; e.currentTarget.style.color = theme?.colors?.primary || '#6366f1'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.color = ''; }}>
    {region}
  </div>
);

// Star Rating
const StarRating = ({ rating, color }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <svg key={i} className="w-4 h-4" style={{ color: i < rating ? '#F59E0B' : '#D1D5DB' }} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

// Testimonial Card
const TestimonialCard = ({ testimonial, primaryColor }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-full hover:shadow-md transition-shadow duration-300">
    {/* Quote icon */}
    <div className="mb-4">
      <svg className="w-8 h-8 opacity-20" style={{ color: primaryColor }} fill="currentColor" viewBox="0 0 24 24">
        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
      </svg>
    </div>

    {/* Stars */}
    <StarRating rating={testimonial.rating} />

    {/* Quote */}
    <p className="text-gray-700 text-sm leading-relaxed mt-3 mb-4 flex-1 italic">
      "{testimonial.quote}"
    </p>

    {/* Property detail chip */}
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium mb-4" style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
      </svg>
      {testimonial.detail}
    </div>

    {/* Author */}
    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: testimonial.avatarBg }}>
        {testimonial.avatar}
      </div>
      <div>
        <p className="font-semibold text-gray-900 text-sm">{testimonial.name}</p>
        <p className="text-gray-500 text-xs">{testimonial.role}</p>
      </div>
    </div>
  </div>
);

const CountryHome = () => {
  const { countryCode, country: countryConfig, theme } = useCountry();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [country, setCountry] = useState(countryService.getCountryFromCache(countryCode));
  const [loading, setLoading] = useState(!countryService.getCountryFromCache(countryCode));
  // Language is set by SubdomainLayout, no need to duplicate here

  // Fetch country data from API
  useEffect(() => {
    const fetchCountryData = async () => {
      try {
        const data = await countryService.getCountryByCode(countryCode);
        if (data) {
          setCountry(data);
        } else {
          setTimeout(() => navigate('/'), 100);
        }
      } catch (error) {
        console.error('Error fetching country:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCountryData();
  }, [countryCode, navigate]);

  // Show loading state
  if (loading && !country) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme?.colors?.heroBg || 'linear-gradient(135deg, #f0f4ff, #e8f0fe)' }}>
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-white/80 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!country) {
    return null;
  }

  const handleGetStarted = () => {
    navigate('/contracts/create');
  };

  const colors = theme?.colors || {};

  return (
    <div className="relative overflow-hidden">
      {/* Hero Section - Full-bleed themed */}
      <section className="relative overflow-hidden" style={{ background: theme ? colors.heroBg : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #6366f1 100%)' }}>
        {/* Large decorative patterns */}
        <div className="absolute inset-0 overflow-hidden">
          {theme && (
            <>
              <div className="absolute -top-20 -right-20 w-[500px] h-[500px] opacity-[0.07]">
                <PatternOverlay pattern={theme.pattern} primaryColor="#ffffff" />
              </div>
              <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] opacity-[0.05] rotate-45">
                <PatternOverlay pattern={theme.pattern} primaryColor="#ffffff" />
              </div>
            </>
          )}
          {/* Gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28">
          <div className="text-center max-w-4xl mx-auto animate-fade-in">

            {/* Law reference badge — local, not "international picker" */}
            {theme?.local ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-6 sm:mb-8">
                <svg className="w-4 h-4 text-white/80 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-white/90 text-xs sm:text-sm font-medium">{theme.local.lawRef}</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-6 sm:mb-8">
                <span className="text-white/90 font-semibold text-sm sm:text-base">{theme?.legalBadge}</span>
              </div>
            )}

            {/* Hero title — local copy if available */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2 drop-shadow-sm">
              {theme?.local?.heroTitle || t('home.hero.title')}
            </h1>

            {/* Local subtitle — specific law, local specifics */}
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-10 sm:mb-12 max-w-3xl mx-auto px-2 leading-relaxed">
              {theme?.local?.heroSubtitle || t('home.hero.subtitle')}
            </p>

            {/* CTA + price comparison */}
            <div className="flex flex-col items-center gap-3 px-4">
              <button
                onClick={handleGetStarted}
                className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 group flex items-center justify-center hover:-translate-y-0.5"
                style={{ background: 'white', color: colors.primary || '#6366f1', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)' }}
              >
                {t('home.hero.cta')}
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              {/* Price comparison — local anchor */}
              {theme?.local?.priceComparison && (
                <p className="text-white/50 text-xs sm:text-sm">
                  {theme.local.priceComparison}
                </p>
              )}
            </div>

            {/* Stats bar — local cities + time + format */}
            <div className="mt-12 sm:mt-16 pt-8 border-t border-white/10">
              {/* Local cities row */}
              {theme?.local?.cities && (
                <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                  {theme.local.cities.map((city, i) => (
                    <span key={city} className="flex items-center gap-2">
                      <span className="text-white/70 text-xs sm:text-sm">{city}</span>
                      {i < theme.local.cities.length - 1 && (
                        <span className="w-1 h-1 rounded-full bg-white/30" />
                      )}
                    </span>
                  ))}
                </div>
              )}
              {/* Metrics row */}
              <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-white">{(country.regions || []).length}+</div>
                  <div className="text-xs sm:text-sm text-white/60 mt-1">{t('home.stats.regionsLabel')}</div>
                </div>
                <div className="w-px h-8 bg-white/20 hidden sm:block" />
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-white">{t('home.stats.timeValue', '15 min')}</div>
                  <div className="text-xs sm:text-sm text-white/60 mt-1">{t('home.stats.timeLabel')}</div>
                </div>
                <div className="w-px h-8 bg-white/20 hidden sm:block" />
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-white">PDF</div>
                  <div className="text-xs sm:text-sm text-white/60 mt-1">{t('home.stats.downloadLabel')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 36.7 768 43.3 864 45C960 46.7 1056 43.3 1152 40C1248 36.7 1344 33.3 1392 31.7L1440 30V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4" style={{ backgroundColor: `${colors.primary || '#6366f1'}10`, color: colors.primary || '#6366f1' }}>
            {t('home.whyChooseUs')}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            {t('home.features.title')}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <FeatureCard
            icon={<svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
            iconBg={`bg-primary-100`}
            title={t('home.features.legallyCompliant.title')}
            description={t('home.features.legallyCompliant.description')}
            accentColor={colors.primary || '#6366f1'}
          />
          <FeatureCard
            icon={<svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
            iconBg="bg-emerald-100"
            title={t('home.features.quickEasy.title')}
            description={t('home.features.quickEasy.description')}
            accentColor={colors.secondary || '#10B981'}
          />
          <FeatureCard
            icon={<svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            iconBg="bg-purple-100"
            title={t('home.features.affordable.title')}
            description={t('home.features.affordable.description')}
            accentColor={colors.accent || '#8B5CF6'}
          />
          <FeatureCard
            icon={<svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
            iconBg="bg-indigo-100"
            title={t('home.features.professional.title')}
            description={t('home.features.professional.description')}
            accentColor={colors.primaryLight || '#4F46E5'}
          />
        </div>
      </section>

      {/* How It Works Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4" style={{ backgroundColor: `${colors.primary || '#6366f1'}10`, color: colors.primary || '#6366f1' }}>
            {t('home.howItWorks.badge')}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            {t('home.howItWorks.title')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 relative">
          {/* Progress Line - themed */}
          <div className="hidden lg:block absolute top-[4rem] left-[20%] right-[20%] h-1 rounded-full" style={{ background: theme ? `linear-gradient(to right, ${colors.primary}30, ${colors.primary}60, ${colors.primary}30)` : 'linear-gradient(to right, var(--primary-200), var(--primary-400), var(--primary-200))' }} />

          <StepCard number={1} title={t('home.howItWorks.step1.title')} description={t('home.howItWorks.step1.description')} theme={theme} />
          <StepCard number={2} title={t('home.howItWorks.step2.title')} description={t('home.howItWorks.step2.description')} theme={theme} />
          <StepCard number={3} title={t('home.howItWorks.step3.title')} description={t('home.howItWorks.step3.description')} isLast theme={theme} />
        </div>
      </section>

      {/* Testimonials Section */}
      {COUNTRY_TESTIMONIALS[countryCode] && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4" style={{ backgroundColor: `${colors.primary || '#6366f1'}10`, color: colors.primary || '#6366f1' }}>
              {t('home.testimonials.badge', 'Customer Reviews')}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('home.testimonials.title', 'Trusted by Landlords Across the Country')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.testimonials.subtitle', 'Join thousands of landlords who save time and money with RentyDoc.')}
            </p>
            {/* Aggregate rating */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <StarRating rating={5} />
              <span className="text-gray-700 font-semibold text-sm">4.9 / 5</span>
              <span className="text-gray-400 text-sm">{t('home.testimonials.reviewCount', '(2,400+ reviews)')}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {COUNTRY_TESTIMONIALS[countryCode].map((testimonial, i) => (
              <TestimonialCard key={i} testimonial={testimonial} primaryColor={colors.primary || '#6366f1'} />
            ))}
          </div>
        </section>
      )}

      {/* Regions Section */}
      <section className="relative py-16 sm:py-20 overflow-hidden" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primary}03 50%, ${colors.accent || colors.primary}06 100%)` : undefined }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
            <div className="text-center mb-8 sm:mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4" style={{ backgroundColor: `${colors.primary || '#6366f1'}10`, color: colors.primary || '#6366f1' }}>
                {t('home.coverage.badge')}
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('home.states.title')}
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                {t('home.states.description')}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto mb-6 sm:mb-8">
              {(country.regions || []).slice(0, 8).map((region) => (
                <RegionBadge key={region} region={region} theme={theme} />
              ))}
            </div>

            {(country.regions || []).length > 8 && (
              <p className="text-center text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">
                {t('home.states.moreRegions', { count: (country.regions || []).length - 8 })}
              </p>
            )}

            <div className="text-center">
              <button
                onClick={handleGetStarted}
                className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 text-base sm:text-lg font-semibold text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                style={{ background: colors.buttonBg || 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: `0 10px 25px -5px ${colors.primary || '#6366f1'}40` }}
                onMouseEnter={(e) => { if (colors.buttonHover) e.currentTarget.style.background = colors.buttonHover; }}
                onMouseLeave={(e) => { if (colors.buttonBg) e.currentTarget.style.background = colors.buttonBg; }}
              >
                {t('common.getStarted')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        {/* Local compliance line */}
        {theme?.local?.trustLine && (
          <div className="text-center mb-6">
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-500 bg-gray-50 rounded-full px-4 py-2 border border-gray-100">
              <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.primary }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {theme.local.trustLine}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
          {/* Law verified badge */}
          {theme?.local?.lawVerified && (
            <div className="trust-badge text-xs sm:text-sm">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" style={{ color: colors.primary || '#10B981' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium text-gray-700">{theme.local.lawVerified}</span>
            </div>
          )}
          <div className="trust-badge text-xs sm:text-sm">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" style={{ color: colors.primary || '#6366f1' }} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium text-gray-700">{t('home.trust.secureEncrypted')}</span>
          </div>
          <div className="trust-badge text-xs sm:text-sm">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" style={{ color: colors.accent || '#F59E0B' }} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="font-medium text-gray-700">{t('home.trust.instantDownload')}</span>
          </div>
        </div>
      </section>

    </div>
  );
};

export default CountryHome;
